import React, { useState, useEffect, useMemo } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  RefreshCw, Search, ChevronDown, ChevronRight,
  Lock, Unlock, ArrowRight, Circle, GitBranch,
  Layers, Server, Database, Monitor, User,
  Bell, Smartphone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_COLORS = {
  GET: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  POST: 'bg-blue-100 text-blue-700 border-blue-300',
  PUT: 'bg-amber-100 text-amber-700 border-amber-300',
  DELETE: 'bg-red-100 text-red-700 border-red-300',
  PATCH: 'bg-purple-100 text-purple-700 border-purple-300',
};

const ACTOR_ICONS = {
  'Browser': Monitor,
  'Browser Gestor': Monitor,
  'Operador': User,
  'Gestor': User,
  'MongoDB': Database,
  'Qualquer actor': User,
};

function MethodBadge({ method }) {
  return (
    <span className={`px-2 py-0.5 text-xs font-mono font-bold rounded border ${METHOD_COLORS[method] || 'bg-stone-100 text-stone-600 border-stone-300'}`}>
      {method}
    </span>
  );
}

function RouterCard({ router, searchTerm }) {
  const [expanded, setExpanded] = useState(false);

  const filteredEndpoints = useMemo(() => {
    if (!searchTerm) return router.endpoints;
    const term = searchTerm.toLowerCase();
    return router.endpoints.filter(ep =>
      ep.path.toLowerCase().includes(term) ||
      ep.descricao.toLowerCase().includes(term) ||
      ep.metodo.toLowerCase().includes(term)
    );
  }, [router.endpoints, searchTerm]);

  if (searchTerm && filteredEndpoints.length === 0) return null;

  return (
    <Card
      data-testid={`router-card-${router.router}`}
      className="overflow-hidden border transition-shadow hover:shadow-md"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: router.cor }}
          />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-stone-900">{router.grupo}</span>
              {router.protegido && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600 border border-red-200">
                  <Lock className="h-2.5 w-2.5" /> JWT
                </span>
              )}
              {!router.protegido && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">
                  <Unlock className="h-2.5 w-2.5" /> Público
                </span>
              )}
            </div>
            <p className="text-xs text-stone-500 mt-0.5">{router.descricao}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono text-stone-500">
            {searchTerm ? `${filteredEndpoints.length}/` : ''}{router.total_endpoints} eps
          </span>
          {expanded ? <ChevronDown className="h-4 w-4 text-stone-400" /> : <ChevronRight className="h-4 w-4 text-stone-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t bg-stone-50/50">
          <div className="divide-y divide-stone-100">
            {filteredEndpoints.map((ep, i) => (
              <div
                key={i}
                className="flex items-start gap-3 px-4 py-2.5 hover:bg-white transition-colors"
                data-testid={`endpoint-row-${router.router}-${i}`}
              >
                <MethodBadge method={ep.metodo} />
                <code className="text-sm font-mono text-stone-800 break-all flex-1">{ep.path}</code>
                {ep.descricao && (
                  <span className="text-xs text-stone-500 shrink-0 max-w-[280px] text-right">{ep.descricao}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function FlowDiagram({ flow }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card data-testid={`flow-${flow.id}`} className="overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <GitBranch className="h-5 w-5 text-primary" />
          <span className="font-semibold text-stone-900">{flow.nome}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-stone-500">{flow.passos.length} passos</span>
          {expanded ? <ChevronDown className="h-4 w-4 text-stone-400" /> : <ChevronRight className="h-4 w-4 text-stone-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t p-4">
          <div className="space-y-0">
            {flow.passos.map((passo, i) => {
              const ActorIcon = ACTOR_ICONS[passo.actor] || Server;
              const TargetIcon = ACTOR_ICONS[passo.target] || Server;
              const isLast = i === flow.passos.length - 1;

              return (
                <div key={i} className="relative">
                  {/* Connector line */}
                  {!isLast && (
                    <div className="absolute left-[19px] top-[40px] bottom-0 w-0.5 bg-stone-200" />
                  )}
                  <div className="flex items-start gap-4 py-3">
                    {/* Step number */}
                    <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center text-sm font-bold text-primary z-10">
                      {i + 1}
                    </div>

                    {/* Flow content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Actor */}
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium border border-blue-200">
                          <ActorIcon className="h-3.5 w-3.5" />
                          {passo.actor}
                        </span>

                        {/* Arrow */}
                        <ArrowRight className="h-4 w-4 text-stone-400 shrink-0" />

                        {/* Action */}
                        <span className="text-sm text-stone-700 font-mono bg-stone-100 px-2 py-1 rounded border border-stone-200">
                          {passo.action}
                        </span>

                        {/* Arrow */}
                        <ArrowRight className="h-4 w-4 text-stone-400 shrink-0" />

                        {/* Target */}
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-sm font-medium border border-amber-200">
                          <TargetIcon className="h-3.5 w-3.5" />
                          {passo.target}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ApiDocumentation({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('endpoints');

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sistema/endpoints`);
      setData(res.data);
    } catch (e) {
      console.error('Erro ao carregar endpoints:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const stats = useMemo(() => {
    if (!data) return {};
    const publicos = data.routers.filter(r => !r.protegido);
    const protegidos = data.routers.filter(r => r.protegido);
    return {
      totalEndpoints: data.total_endpoints,
      totalRouters: data.total_routers,
      publicos: publicos.reduce((acc, r) => acc + r.total_endpoints, 0),
      protegidos: protegidos.reduce((acc, r) => acc + r.total_endpoints, 0),
      flows: data.fluxos?.length || 0,
    };
  }, [data]);

  const content = (
      <div className="space-y-6" data-testid="api-documentation-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-stone-900 flex items-center gap-2">
              <Layers className="h-6 w-6 text-primary" />
              Documentação da API
            </h1>
            <p className="text-stone-500 text-sm mt-1">
              Todos os endpoints e fluxos de informação do sistema
            </p>
          </div>
          <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-api-docs">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 text-stone-400 animate-spin" />
          </div>
        ) : data && (
          <>
            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: 'Routers', value: stats.totalRouters, color: 'bg-blue-50 text-blue-700 border-blue-200' },
                { label: 'Endpoints', value: stats.totalEndpoints, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                { label: 'Públicos', value: stats.publicos, color: 'bg-green-50 text-green-700 border-green-200' },
                { label: 'Protegidos', value: stats.protegidos, color: 'bg-red-50 text-red-700 border-red-200' },
                { label: 'Fluxos', value: stats.flows, color: 'bg-purple-50 text-purple-700 border-purple-200' },
              ].map((s, i) => (
                <Card key={i} className={`p-3 border ${s.color}`}>
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs font-medium opacity-80">{s.label}</p>
                </Card>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b">
              {[
                { id: 'endpoints', label: 'Endpoints', icon: Server },
                { id: 'fluxos', label: 'Fluxos de Informação', icon: GitBranch },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-[1px] ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Endpoints Tab */}
            {activeTab === 'endpoints' && (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    data-testid="search-endpoints"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Pesquisar endpoint... (ex: /login, GET, anomalias)"
                    className="pl-10"
                  />
                </div>

                {/* Router list */}
                <div className="space-y-3">
                  {data.routers.map(router => (
                    <RouterCard key={router.router} router={router} searchTerm={search} />
                  ))}
                </div>
              </div>
            )}

            {/* Flows Tab */}
            {activeTab === 'fluxos' && (
              <div className="space-y-4">
                <p className="text-sm text-stone-500">
                  Diagramas dos principais fluxos de dados entre os componentes do sistema.
                </p>
                {data.fluxos.map(flow => (
                  <FlowDiagram key={flow.id} flow={flow} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
  );

  return embedded ? content : <GestorLayout>{content}</GestorLayout>;
}
